import { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { TrendingUp, TrendingDown, Minus, Scale } from 'lucide-react';

// Custom tooltip component
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 shadow-lg rounded-lg border border-earth-200">
        <p className="text-sm text-earth-600">{label}</p>
        <p className="text-lg font-semibold text-primary-900">
          {payload[0].value} kg
        </p>
        {payload[0].payload.source && (
          <p className="text-xs text-earth-500">{payload[0].payload.source}</p>
        )}
      </div>
    );
  }
  return null;
};

export default function WeightChart({ weightHistory = [], currentWeight, targetWeight }) {
  // Process data for chart
  const chartData = useMemo(() => {
    if (!weightHistory || weightHistory.length === 0) {
      return [];
    }

    return weightHistory
      .map(record => ({
        date: format(parseISO(record.timestamp || record.date), 'MMM d'),
        fullDate: record.timestamp || record.date,
        weight: parseFloat(record.weight_kg),
        source: record.device || record.source || 'Manual',
      }))
      .sort((a, b) => new Date(a.fullDate) - new Date(b.fullDate));
  }, [weightHistory]);

  // Calculate statistics
  const stats = useMemo(() => {
    if (chartData.length === 0) {
      return {
        change: 0,
        changePercent: 0,
        trend: 'stable',
        avgDaily: 0,
        min: currentWeight || 0,
        max: currentWeight || 0,
      };
    }

    const weights = chartData.map(d => d.weight);
    const firstWeight = weights[0];
    const lastWeight = weights[weights.length - 1];
    const change = lastWeight - firstWeight;
    const changePercent = firstWeight > 0 ? ((change / firstWeight) * 100).toFixed(1) : 0;

    // Calculate days between first and last reading
    const firstDate = new Date(chartData[0].fullDate);
    const lastDate = new Date(chartData[chartData.length - 1].fullDate);
    const daysDiff = Math.max(1, Math.ceil((lastDate - firstDate) / (1000 * 60 * 60 * 24)));
    const avgDaily = (change / daysDiff).toFixed(2);

    return {
      change: change.toFixed(1),
      changePercent,
      trend: change > 2 ? 'up' : change < -2 ? 'down' : 'stable',
      avgDaily,
      min: Math.min(...weights),
      max: Math.max(...weights),
    };
  }, [chartData, currentWeight]);

  // If no weight history, show placeholder
  if (chartData.length === 0) {
    return (
      <div className="card p-6">
        <h3 className="text-lg font-medium text-primary-900 mb-4">Weight History</h3>
        <div className="h-48 flex flex-col items-center justify-center text-earth-500">
          <Scale className="h-12 w-12 mb-3 opacity-30" />
          <p>No weight records available</p>
          <p className="text-sm mt-1">
            {currentWeight ? `Current: ${currentWeight} kg` : 'Record weights to see trends'}
          </p>
        </div>
      </div>
    );
  }

  const TrendIcon = stats.trend === 'up' ? TrendingUp : stats.trend === 'down' ? TrendingDown : Minus;
  const trendColor = stats.trend === 'up' ? 'text-green-600' : stats.trend === 'down' ? 'text-red-600' : 'text-earth-500';

  return (
    <div className="card p-6">
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-lg font-medium text-primary-900">Weight History</h3>
        <div className={`flex items-center gap-1 ${trendColor}`}>
          <TrendIcon className="h-4 w-4" />
          <span className="text-sm font-medium">
            {stats.change > 0 ? '+' : ''}{stats.change} kg ({stats.changePercent}%)
          </span>
        </div>
      </div>

      {/* Chart */}
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis 
              dataKey="date" 
              tick={{ fontSize: 12, fill: '#6b7280' }}
              tickLine={false}
            />
            <YAxis 
              domain={['auto', 'auto']}
              tick={{ fontSize: 12, fill: '#6b7280' }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => `${value}kg`}
            />
            <Tooltip content={<CustomTooltip />} />
            {targetWeight && (
              <ReferenceLine 
                y={targetWeight} 
                stroke="#3b82f6" 
                strokeDasharray="5 5"
                label={{ value: 'Target', fill: '#3b82f6', fontSize: 12 }}
              />
            )}
            <Line
              type="monotone"
              dataKey="weight"
              stroke="#2d4a3e"
              strokeWidth={2}
              dot={{ fill: '#2d4a3e', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, fill: '#b8954f' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-4 gap-4 mt-4 pt-4 border-t border-earth-200">
        <div className="text-center">
          <div className="text-xs text-earth-500 uppercase">Current</div>
          <div className="text-lg font-semibold text-primary-900">
            {currentWeight || chartData[chartData.length - 1]?.weight || '—'} kg
          </div>
        </div>
        <div className="text-center">
          <div className="text-xs text-earth-500 uppercase">Min</div>
          <div className="text-lg font-semibold text-primary-900">{stats.min} kg</div>
        </div>
        <div className="text-center">
          <div className="text-xs text-earth-500 uppercase">Max</div>
          <div className="text-lg font-semibold text-primary-900">{stats.max} kg</div>
        </div>
        <div className="text-center">
          <div className="text-xs text-earth-500 uppercase">Avg/Day</div>
          <div className={`text-lg font-semibold ${trendColor}`}>
            {stats.avgDaily > 0 ? '+' : ''}{stats.avgDaily} kg
          </div>
        </div>
      </div>
    </div>
  );
}
